import React, { useState, useEffect } from 'react';
import {
  ShoppingCart, Plus, Trash2, RotateCcw, Check, Store,
  ChevronDown, ChevronUp, Pencil, X, Save,
} from 'lucide-react';

// ── Types ──────────────────────────────────────────────────────────────────────

interface ShoppingItem {
  id: string;
  name: string;
  quantity: number;
  unit?: string;
  checked: boolean;
  variable?: boolean; // cantidad variable semana a semana
}

interface ShoppingStore {
  id: string;
  name: string;
  color: string;
  items: ShoppingItem[];
}

// ── Initial Data ───────────────────────────────────────────────────────────────

const STORAGE_KEY = 'dufi-shopping-list';

const defaultStores: ShoppingStore[] = [
  {
    id: 'olimpica',
    name: 'OLIMPICA',
    color: 'bg-red-500',
    items: [
      { id: 'ol-1', name: 'Aceite', quantity: 1, unit: 'und', checked: false },
      { id: 'ol-2', name: 'Manzana', quantity: 1, unit: 'kg', checked: false },
      { id: 'ol-3', name: 'Leche', quantity: 1, unit: 'lt', checked: false },
      { id: 'ol-4', name: 'Azúcar', quantity: 1, unit: 'kg', checked: false },
      { id: 'ol-5', name: 'Chocolatina', quantity: 1, unit: 'und', checked: false },
    ],
  },
  {
    id: 'd1',
    name: 'D1',
    color: 'bg-yellow-500',
    items: [
      { id: 'd1-1', name: 'Esponja de alambre', quantity: 1, unit: 'und', checked: false },
      { id: 'd1-2', name: 'Lechuga crespa', quantity: 1, unit: 'und', checked: false },
      { id: 'd1-3', name: 'Jabón en polvo', quantity: 1, unit: 'und', checked: false },
    ],
  },
  {
    id: 'ara',
    name: 'Ara',
    color: 'bg-green-500',
    items: [
      { id: 'ara-1', name: 'Jabón rey', quantity: 1, unit: 'und', checked: false },
    ],
  },
  {
    id: 'estanco',
    name: 'Estanco',
    color: 'bg-purple-500',
    items: [
      { id: 'es-1',  name: 'Chorizos',              quantity: 5,  unit: 'und', checked: false },
      { id: 'es-2',  name: 'Suizo',                 quantity: 5,  unit: 'und', checked: false },
      { id: 'es-3',  name: 'Salchicha americana',   quantity: 20, unit: 'und', checked: false },
      { id: 'es-4',  name: 'Salsa',                 quantity: 1,  unit: 'und', checked: false },
      { id: 'es-5',  name: 'BBQ',                   quantity: 1,  unit: 'und', checked: false },
      { id: 'es-6',  name: 'Rosada',                quantity: 1,  unit: 'und', checked: false },
      { id: 'es-7',  name: 'Tomate',                quantity: 1,  unit: 'kg',  checked: false },
      { id: 'es-8',  name: 'Pan Hamburguesa',        quantity: 1,  unit: 'paq', checked: false },
      { id: 'es-9',  name: 'Jamón',                 quantity: 1,  unit: 'und', checked: false },
      { id: 'es-10', name: 'Mantequilla',            quantity: 1,  unit: 'und', checked: false },
      { id: 'es-11', name: 'Tocineta',              quantity: 1,  unit: 'und', checked: false },
      { id: 'es-12', name: 'Chip de chocolate',     quantity: 1,  unit: 'und', checked: false },
      { id: 'es-13', name: 'Pulpo o sapito',        quantity: 1,  unit: 'und', checked: false },
      { id: 'es-14', name: 'Galleta Oreo',          quantity: 1,  unit: 'paq', checked: false },
      { id: 'es-15', name: 'Chantilly',             quantity: 1,  unit: 'und', checked: false },
      { id: 'es-16', name: 'Barquillo',             quantity: 1,  unit: 'paq', checked: false },
      { id: 'es-17', name: 'Chocolita',             quantity: 1,  unit: 'und', checked: false },
      { id: 'es-18', name: 'Bretaña',               quantity: 1,  unit: 'und', checked: false },
      { id: 'es-19', name: 'Cuchara grande',        quantity: 1,  unit: 'paq', checked: false },
      { id: 'es-20', name: 'Tenedores',             quantity: 1,  unit: 'paq', checked: false },
      { id: 'es-21', name: 'Cuchillo',              quantity: 1,  unit: 'paq', checked: false },
      { id: 'es-22', name: 'Plato ondo #12',        quantity: 1,  unit: 'paq', checked: false },
      { id: 'es-23', name: 'Masmelos',              quantity: 1,  unit: 'und', checked: false, variable: true },
    ],
  },
];

// ── Helpers ────────────────────────────────────────────────────────────────────

function uid() {
  return Math.random().toString(36).slice(2, 9);
}

function loadStores(): ShoppingStore[] {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) return JSON.parse(raw);
  } catch {}
  return defaultStores;
}

function saveStores(stores: ShoppingStore[]) {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(stores));
}

// ── Sub-components ─────────────────────────────────────────────────────────────

interface ItemRowProps {
  item: ShoppingItem;
  onToggle: () => void;
  onQtyChange: (delta: number) => void;
  onDelete: () => void;
  onRename: (name: string) => void;
}

function ItemRow({ item, onToggle, onQtyChange, onDelete, onRename }: ItemRowProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.name);

  const commitRename = () => {
    if (draft.trim()) onRename(draft.trim());
    setEditing(false);
  };

  return (
    <div
      className={`flex items-center gap-2 rounded-lg px-3 py-2 transition-colors ${
        item.checked ? 'bg-gray-100 dark:bg-gray-800 opacity-60' : 'bg-white dark:bg-gray-900'
      } border border-gray-100 dark:border-gray-700`}
    >
      {/* Checkbox */}
      <button
        onClick={onToggle}
        className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full border-2 transition-colors ${
          item.checked
            ? 'border-emerald-500 bg-emerald-500 text-white'
            : 'border-gray-300 dark:border-gray-600'
        }`}
      >
        {item.checked && <Check size={13} strokeWidth={3} />}
      </button>

      {/* Name */}
      <div className="flex flex-1 items-center gap-1 min-w-0">
        {editing ? (
          <input
            autoFocus
            value={draft}
            onChange={e => setDraft(e.target.value)}
            onBlur={commitRename}
            onKeyDown={e => e.key === 'Enter' && commitRename()}
            className="flex-1 rounded border border-blue-400 px-2 py-0.5 text-sm outline-none dark:bg-gray-800 dark:text-white"
          />
        ) : (
          <span
            className={`flex-1 truncate text-sm font-medium ${
              item.checked ? 'line-through text-gray-400' : 'text-gray-800 dark:text-gray-100'
            }`}
          >
            {item.name}
            {item.variable && (
              <span className="ml-1 text-xs text-amber-500 font-normal">(variable)</span>
            )}
          </span>
        )}
        {!editing && (
          <button
            onClick={() => { setDraft(item.name); setEditing(true); }}
            className="shrink-0 p-1 text-gray-400 hover:text-blue-500 transition-colors"
          >
            <Pencil size={12} />
          </button>
        )}
      </div>

      {/* Quantity controls */}
      <div className="flex items-center gap-1 shrink-0">
        <button
          onClick={() => onQtyChange(-1)}
          className="h-6 w-6 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 font-bold text-sm flex items-center justify-center"
        >
          −
        </button>
        <span className="w-8 text-center text-sm font-semibold text-gray-700 dark:text-gray-200">
          {item.quantity}
        </span>
        <button
          onClick={() => onQtyChange(1)}
          className="h-6 w-6 rounded bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600 font-bold text-sm flex items-center justify-center"
        >
          +
        </button>
        {item.unit && (
          <span className="w-8 text-xs text-gray-400">{item.unit}</span>
        )}
      </div>

      {/* Delete */}
      <button
        onClick={onDelete}
        className="shrink-0 p-1 text-gray-300 hover:text-red-500 transition-colors"
      >
        <Trash2 size={14} />
      </button>
    </div>
  );
}

// ── Store Card ─────────────────────────────────────────────────────────────────

interface StoreCardProps {
  store: ShoppingStore;
  onChange: (store: ShoppingStore) => void;
  onDelete: () => void;
}

function StoreCard({ store, onChange, onDelete }: StoreCardProps) {
  const [collapsed, setCollapsed] = useState(false);
  const [newItem, setNewItem] = useState('');
  const [addingUnit, setAddingUnit] = useState('');

  const checkedCount = store.items.filter(i => i.checked).length;
  const total = store.items.length;

  const updateItem = (id: string, patch: Partial<ShoppingItem>) => {
    onChange({
      ...store,
      items: store.items.map(i => i.id === id ? { ...i, ...patch } : i),
    });
  };

  const addItem = () => {
    if (!newItem.trim()) return;
    onChange({
      ...store,
      items: [
        ...store.items,
        { id: uid(), name: newItem.trim(), quantity: 1, unit: addingUnit || undefined, checked: false },
      ],
    });
    setNewItem('');
    setAddingUnit('');
  };

  const deleteItem = (id: string) => {
    onChange({ ...store, items: store.items.filter(i => i.id !== id) });
  };

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-700 overflow-hidden shadow-sm">
      {/* Header */}
      <div
        className={`flex items-center justify-between px-4 py-3 cursor-pointer select-none ${store.color}`}
        onClick={() => setCollapsed(c => !c)}
      >
        <div className="flex items-center gap-2">
          <Store size={16} className="text-white" />
          <span className="font-bold text-white text-sm tracking-wide">{store.name}</span>
          <span className="rounded-full bg-white/20 px-2 py-0.5 text-xs text-white font-medium">
            {checkedCount}/{total}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={e => { e.stopPropagation(); onDelete(); }}
            className="p-1 text-white/60 hover:text-white transition-colors"
          >
            <X size={14} />
          </button>
          {collapsed ? <ChevronDown size={16} className="text-white" /> : <ChevronUp size={16} className="text-white" />}
        </div>
      </div>

      {/* Progress bar */}
      <div className="h-1 bg-gray-100 dark:bg-gray-800">
        <div
          className={`h-1 transition-all duration-300 ${store.color}`}
          style={{ width: total ? `${(checkedCount / total) * 100}%` : '0%' }}
        />
      </div>

      {/* Items */}
      {!collapsed && (
        <div className="bg-gray-50 dark:bg-gray-900 p-3 flex flex-col gap-2">
          {store.items.map(item => (
            <ItemRow
              key={item.id}
              item={item}
              onToggle={() => updateItem(item.id, { checked: !item.checked })}
              onQtyChange={delta =>
                updateItem(item.id, { quantity: Math.max(1, item.quantity + delta) })
              }
              onDelete={() => deleteItem(item.id)}
              onRename={name => updateItem(item.id, { name })}
            />
          ))}

          {/* Add item */}
          <div className="flex gap-2 mt-1">
            <input
              value={newItem}
              onChange={e => setNewItem(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && addItem()}
              placeholder="Agregar producto..."
              className="flex-1 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-3 py-1.5 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 outline-none focus:border-blue-400"
            />
            <input
              value={addingUnit}
              onChange={e => setAddingUnit(e.target.value)}
              placeholder="und"
              className="w-14 rounded-lg border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 px-2 py-1.5 text-sm text-center text-gray-700 dark:text-gray-200 placeholder-gray-400 outline-none focus:border-blue-400"
            />
            <button
              onClick={addItem}
              className="rounded-lg bg-blue-500 px-3 py-1.5 text-white hover:bg-blue-600 transition-colors"
            >
              <Plus size={16} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Page ──────────────────────────────────────────────────────────────────

const STORE_COLORS = [
  'bg-red-500', 'bg-blue-500', 'bg-green-500', 'bg-purple-500',
  'bg-orange-500', 'bg-pink-500', 'bg-teal-500', 'bg-indigo-500',
];

export default function ShoppingChecklist() {
  const [stores, setStores] = useState<ShoppingStore[]>(loadStores);
  const [newStoreName, setNewStoreName] = useState('');
  const [colorIdx, setColorIdx] = useState(0);
  const [showConfirmReset, setShowConfirmReset] = useState(false);

  // Persist on every change
  useEffect(() => {
    saveStores(stores);
  }, [stores]);

  const updateStore = (id: string, updated: ShoppingStore) => {
    setStores(prev => prev.map(s => s.id === id ? updated : s));
  };

  const deleteStore = (id: string) => {
    setStores(prev => prev.filter(s => s.id !== id));
  };

  const addStore = () => {
    if (!newStoreName.trim()) return;
    setStores(prev => [
      ...prev,
      {
        id: uid(),
        name: newStoreName.trim().toUpperCase(),
        color: STORE_COLORS[colorIdx % STORE_COLORS.length],
        items: [],
      },
    ]);
    setNewStoreName('');
    setColorIdx(i => i + 1);
  };

  const resetChecks = () => {
    setStores(prev =>
      prev.map(s => ({ ...s, items: s.items.map(i => ({ ...i, checked: false })) }))
    );
    setShowConfirmReset(false);
  };

  const totalItems = stores.reduce((a, s) => a + s.items.length, 0);
  const checkedItems = stores.reduce((a, s) => a + s.items.filter(i => i.checked).length, 0);

  return (
    <div className="mx-auto max-w-2xl space-y-4 pb-8">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-500 text-white">
            <ShoppingCart size={20} />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Lista de Compras</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {checkedItems} de {totalItems} productos listos
            </p>
          </div>
        </div>

        {/* Reset button */}
        <button
          onClick={() => setShowConfirmReset(true)}
          className="flex items-center gap-1.5 rounded-lg border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
        >
          <RotateCcw size={14} />
          Nuevo fin de semana
        </button>
      </div>

      {/* Global progress */}
      <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-4">
        <div className="mb-2 flex justify-between text-xs text-gray-500 dark:text-gray-400">
          <span>Progreso total</span>
          <span>{totalItems ? Math.round((checkedItems / totalItems) * 100) : 0}%</span>
        </div>
        <div className="h-3 w-full rounded-full bg-gray-100 dark:bg-gray-800">
          <div
            className="h-3 rounded-full bg-emerald-500 transition-all duration-500"
            style={{ width: totalItems ? `${(checkedItems / totalItems) * 100}%` : '0%' }}
          />
        </div>
      </div>

      {/* Store cards */}
      {stores.map(store => (
        <StoreCard
          key={store.id}
          store={store}
          onChange={updated => updateStore(store.id, updated)}
          onDelete={() => deleteStore(store.id)}
        />
      ))}

      {/* Add store */}
      <div className="flex gap-2">
        <input
          value={newStoreName}
          onChange={e => setNewStoreName(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && addStore()}
          placeholder="Agregar tienda..."
          className="flex-1 rounded-xl border border-dashed border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 px-4 py-2.5 text-sm text-gray-700 dark:text-gray-200 placeholder-gray-400 outline-none focus:border-blue-400"
        />
        <button
          onClick={addStore}
          className="rounded-xl bg-blue-500 px-4 py-2.5 text-white hover:bg-blue-600 transition-colors flex items-center gap-1.5 text-sm font-medium"
        >
          <Plus size={16} /> Tienda
        </button>
      </div>

      {/* Reset confirm modal */}
      {showConfirmReset && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-2xl bg-white dark:bg-gray-900 p-6 shadow-xl">
            <h2 className="mb-2 text-lg font-bold text-gray-900 dark:text-white">¿Nuevo fin de semana?</h2>
            <p className="mb-6 text-sm text-gray-500 dark:text-gray-400">
              Se desmarcarán todos los productos. La lista de productos se mantiene intacta.
            </p>
            <div className="flex gap-3">
              <button
                onClick={() => setShowConfirmReset(false)}
                className="flex-1 rounded-xl border border-gray-200 dark:border-gray-700 py-2.5 text-sm font-medium text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
              >
                Cancelar
              </button>
              <button
                onClick={resetChecks}
                className="flex-1 rounded-xl bg-blue-500 py-2.5 text-sm font-medium text-white hover:bg-blue-600 transition-colors"
              >
                Reiniciar
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
